import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Check, Link2, Sparkles, Users, Mail, QrCode, Compass } from "lucide-react";
import { Logo } from "@/components/Logo";
import { MobileMenu } from "@/components/MobileMenu";
import { AccountMenu } from "@/components/AccountMenu";
import { useAuth } from "@/context/AuthContext";
import { login } from "@/lib/api";
import { BillingToggle, PriceLine } from "@/components/BillingToggle";

const features = [
  { icon: Link2, title: "One branded link", body: "Every church gets its own public assessment URL and QR code. No app downloads, no accounts for volunteers.", span: "md:col-span-2" },
  { icon: Sparkles, title: "Gifts + DISC", body: "72 spiritual-gift statements across 24 gifts, plus a 24-statement DISC profile.", span: "" },
  { icon: Compass, title: "Top 3 team matches", body: "Each team is tuned to the gifts and personality it leans on. Volunteers see where they'll thrive instantly.", span: "" },
  { icon: Mail, title: "Results in your inbox", body: "Every completion is emailed to your team with DISC, top gifts and best-fit teams.", span: "" },
  { icon: Users, title: "Your teams, your way", body: "Start with 16 proven defaults, then rename, retune, add or remove to match your church.", span: "" },
];

const tiers = [
  { id: "free", name: "Free", price: "$0", note: "For individuals", perks: ["DISC Personality Assessment", "DISC personality results and explanations", "Downloadable DISC results PDF"], cta: "Take the Free DISC Assessment", to: "/free-assessment" },
  { id: "basic", name: "Basic", amounts: { month: 29, year: 313.2 }, sub: "Church dashboard and ministry matching", perks: ["Everything in Free", "Branded church assessment link + QR code", "Church administrator dashboard", "Volunteer assessment records", "Ministry matching recommendations", "Customizable ministry teams", "Volunteer onboarding and approval workflow"], cta: "Start Basic" },
  { id: "pro", name: "Pro", amounts: { month: 39, year: 421.2 }, sub: "Everything in Basic + Planning Center integration", perks: ["Everything in Basic", "Classic & Studio Themes — Customize your dashboard and volunteer assessment experience", "Planning Center People integration", "Staff accounts (admin or view-only)", "Advanced volunteer reporting and filters", "Church URL settings", "English + Spanish links, AI summaries & serving coach"], cta: "Start Pro", highlight: true },
];

const StartBtn = ({ t }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const start = () => { sessionStorage.setItem("dt_interval", t.interval); user ? navigate("/onboarding") : login("/onboarding"); };
  return <button onClick={start} className={`mt-8 ${t.highlight ? "btn-ghost !text-navy" : "btn-navy"}`} data-testid={`pricing-${t.id}-btn`}>{t.cta}</button>;
};

const Tier = ({ t }) => (
  <div className={`rounded-2xl p-8 lift flex flex-col ${t.highlight ? "bg-navy text-white" : "card-soft"}`} data-testid={`pricing-${t.id}`}>
    <p className={`font-display text-lg font-semibold ${t.highlight ? "" : "text-navy"}`}>{t.name}</p>
    {t.amounts ? <>
      <PriceLine amount={t.amounts[t.interval]} interval={t.interval} light={t.highlight} testid={`pricing-${t.id}-price`} />
      <p className={`text-sm mt-3 ${t.highlight ? "text-white/80" : "text-navy"}`}>{t.sub}</p>
    </> : <p className={`font-display text-5xl font-bold mt-4 ${t.highlight ? "" : "text-navy"}`}>{t.price}<span className={`text-base font-normal ml-1 ${t.highlight ? "text-white/60" : "text-muted-ink"}`}>{t.note}</span></p>}
    <ul className="space-y-2 mt-6 flex-1">
      {t.perks.map((p) => <li key={p} className={`flex gap-2 text-sm ${t.highlight ? "text-white/80" : "text-muted-ink"}`}><Check size={16} className="text-teal shrink-0 mt-0.5" />{p}</li>)}
    </ul>
    {t.to ? <Link to={t.to} className="btn-ghost text-center mt-8" data-testid={`pricing-${t.id}-btn`}>{t.cta}</Link>
      : <StartBtn t={t} />}
  </div>
);

const steps = [
  ["01", "Set up your church", "Sign in with Google, name your church, and your teams are pre-loaded."],
  ["02", "Share your link", "Put the link or QR code in the bulletin, on screens, or in your growth track."],
  ["03", "Place with confidence", "See every volunteer's gifts, personality and best-fit teams in one dashboard."],
];

const HeaderActions = () => {
  const { user } = useAuth();
  return (
    <div className="hidden md:flex gap-3">
      {user ? <AccountMenu /> : <button onClick={login} data-testid="header-sign-in-btn" className="chip-btn !px-5 !py-2 !text-[15px]">Sign in</button>}
      <Link to="/#pricing" data-testid="header-start-btn" className="btn-teal">Get started</Link>
    </div>
  );
};

export const Nav = () => (
  <header className="bg-white/95 backdrop-blur border-b border-line sticky top-0 z-30">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center">
      <Logo />
      <nav className="hidden md:flex gap-9 mx-auto text-[15px] text-navy">
        <Link to="/#how" className="hover:text-teal transition-colors" data-testid="nav-how">How it works</Link>
        <Link to="/#features" className="hover:text-teal transition-colors" data-testid="nav-features">Features</Link>
        <Link to="/#gifts" className="hover:text-teal transition-colors" data-testid="nav-gifts-section">Gifts</Link>
        <Link to="/#pricing" className="hover:text-teal transition-colors" data-testid="nav-pricing">Pricing</Link>
        <Link to="/sample-results" className="hover:text-teal transition-colors" data-testid="nav-sample-results">Sample Results</Link>
      </nav>
      <HeaderActions />
      <MobileMenu />
    </div>
  </header>
);

export const Footer = () => (
  <footer className="border-t border-line">
    <div className="max-w-6xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-sm text-muted-ink">
      <Logo />
      <p>Helping every church build its Dream Team.</p>
      <Link to="/" className="hover:text-teal">© {new Date().getFullYear()} Dream Teams</Link>
    </div>
  </footer>
);

export default function Landing() {
  const [interval, setBillingInterval] = useState("year");
  const { hash, key } = useLocation();
  useEffect(() => {
    if (hash) setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 100);
  }, [hash, key]);
  return (
    <div className="bg-paper min-h-screen">
      <Nav />
      <section className="relative grain overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 pt-28 pb-24 text-center relative">
          <p className="eyebrow fade-up">Volunteer placement made easy</p>
          <h1 className="font-display text-navy font-semibold tracking-tight text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] fade-up d1">
            Place every volunteer<br />
            <span className="font-accent text-teal font-semibold">where they thrive.</span>
          </h1>
          <p className="text-muted-ink text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mt-8 fade-up d2">
            The online assessment that matches every volunteer to the Dream Team where they'll thrive, based on their spiritual gifts and personality. All through a custom link branded to your church.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-12 fade-up d3">
            <a href="#pricing" className="btn-navy flex items-center gap-2" data-testid="hero-get-started-btn">
              Set up your church <ArrowRight size={18} />
            </a>
            <a href="#how" className="btn-ghost" data-testid="hero-how-btn">See how it works</a>
            <Link to="/free-assessment" className="btn-navy !bg-teal" data-testid="hero-free-assessment-btn">Try the free assessment</Link>
          </div>
          <p className="text-sm text-muted-ink mt-6 fade-up d4">Free DISC personality assessment · Church plans from $29/month</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 -mt-4">
        <div className="relative rounded-[28px] overflow-hidden bg-teal p-3 md:p-4 fade-up d4">
          <img src="/hero-dream-team.webp" data-testid="hero-volunteers-img" width="1672" height="941" fetchpriority="high"
            alt="Four smiling young adults outside a church holding You Belong Here, Welcome Home and Your Story Matters signs that say Join A Dream Team"
            className="w-full h-auto aspect-[1672/941] object-contain rounded-[20px]" />
          <div className="relative mt-3 md:mt-0 md:absolute md:left-12 md:bottom-12 bg-white rounded-2xl p-3.5 md:p-5 shadow-xl md:max-w-xs">
            <p className="eyebrow">Sample match</p>
            <p className="font-display text-navy text-xl font-semibold mt-2">First Impressions</p>
            <p className="text-sm text-muted-ink mt-1">Hospitality · Evangelism · I/S profile</p>
            <div className="h-2 bg-teal-soft rounded-full mt-4"><div className="h-2 bg-teal rounded-full w-[88%]" /></div>
            <p className="text-xs text-teal mt-2 font-medium">88% fit</p>
          </div>
        </div>
      </section>

      <section id="how" className="max-w-6xl mx-auto px-6 py-28">
        <p className="eyebrow">How it works</p>
        <h2 className="font-display text-navy text-3xl md:text-4xl font-semibold tracking-tight mt-3 max-w-xl">
          From "I'd love to serve" to <span className="font-accent text-teal">"I found my team"</span> in fifteen minutes.
        </h2>
        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {steps.map(([n, t, b]) => (
            <div key={n} className="card-soft p-8 lift" data-testid={`step-${n}`}>
              <span className="font-mono-dt text-teal text-sm">{n}</span>
              <h3 className="font-display text-navy text-xl font-semibold mt-4">{t}</h3>
              <p className="text-muted-ink mt-3 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="free" className="max-w-6xl mx-auto px-6 pb-28" data-testid="free-assessment-section">
        <div className="rounded-[28px] bg-teal-soft p-10 md:p-14 grid md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-3">
            <p className="eyebrow">Free · No sign-up</p>
            <h2 className="font-display text-navy text-3xl md:text-4xl font-semibold tracking-tight mt-3">
              Try the free <span className="font-accent text-teal">assessment.</span>
            </h2>
            <p className="text-muted-ink text-lg leading-relaxed mt-5">
              Discover your DISC personality in about 5 minutes with 24 statements, in English or Spanish. You get your personality results, explanations and a PDF to keep.
            </p>
            <Link to="/free-assessment" className="btn-navy inline-flex items-center gap-2 mt-8" data-testid="free-assessment-cta">
              Take the Free DISC Assessment <ArrowRight size={18} />
            </Link>
          </div>
          <div className="md:col-span-2 space-y-3">
            {["DISC personality profile", "Primary and secondary personality types", "Explanations, examples & Scripture", "Downloadable PDF"].map((x) => (
              <div key={x} className="bg-white rounded-xl px-5 py-4 text-navy font-medium flex items-center gap-3"><span className="h-2 w-2 rounded-full bg-teal" />{x}</div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-6 py-28">
          <p className="eyebrow !text-[#8FD3C6]">Everything you need</p>
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-3 max-w-2xl">
            Built for pastors and team leaders who'd rather <span className="font-accent text-[#8FD3C6]">shepherd</span> than shuffle spreadsheets.
          </h2>
          <div className="grid md:grid-cols-3 gap-5 mt-14">
            {features.map(({ icon: Icon, title, body, span }) => (
              <div key={title} className={`rounded-2xl border border-white/10 bg-white/[0.04] p-8 lift ${span}`}>
                <Icon className="text-[#8FD3C6]" size={22} />
                <h3 className="font-display text-xl font-semibold mt-5">{title}</h3>
                <p className="text-white/70 mt-3 leading-relaxed">{body}</p>
              </div>
            ))}
            <div className="rounded-2xl bg-teal p-8 flex flex-col justify-between lift">
              <QrCode size={22} />
              <div>
                <h3 className="font-display text-xl font-semibold mt-5">Print a QR code</h3>
                <p className="text-white/85 mt-3">Drop it on a slide and watch the results roll in.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="gifts" className="max-w-6xl mx-auto px-6 py-28 grid md:grid-cols-5 gap-12 items-center">
        <div className="md:col-span-3">
          <p className="eyebrow">Discover your design</p>
          <h2 className="font-display text-navy text-3xl md:text-4xl font-semibold tracking-tight mt-3">
            24 spiritual gifts. Four ways of working. <span className="font-accent text-teal">One body.</span>
          </h2>
          <p className="text-muted-ink text-lg leading-relaxed mt-6">
            Administration, Hospitality, Mercy, Teaching, Leadership, Music, Craftsmanship and more, paired with a DISC profile that shows how each person shows up on a team.
          </p>
          <a href="#pricing" className="btn-navy mt-10 flex items-center gap-2 w-fit" data-testid="gifts-cta-btn">
            Start for your church <ArrowRight size={18} />
          </a>
        </div>
        <div className="md:col-span-2 grid grid-cols-2 gap-4">
          {[["D", "Dominant"], ["I", "Influential"], ["S", "Steady"], ["C", "Conscientious"]].map(([k, n], i) => (
            <div key={k} className={`card-soft p-6 lift ${i % 2 ? "mt-8" : ""}`}>
              <span className="font-display text-5xl font-bold text-teal">{k}</span>
              <p className="text-navy font-medium mt-2">{n}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className="max-w-6xl mx-auto px-6 pb-28" data-testid="pricing-section">
        <p className="eyebrow">Pricing</p>
        <h2 className="font-display text-navy text-3xl md:text-4xl font-semibold tracking-tight mt-3">
          Simple plans for <span className="font-accent text-teal">every church.</span>
        </h2>
        <p className="text-muted-ink mt-4">Have a discount code? Enter it at checkout.</p>
        <div className="mt-8 flex justify-center"><BillingToggle interval={interval} onChange={setBillingInterval} /></div>
        <div className="grid md:grid-cols-3 gap-6 mt-6">{tiers.map((t) => <Tier key={t.id} t={{ ...t, interval }} />)}</div>
      </section>

      <Footer />
    </div>
  );
}
